import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { FC, useState, useCallback, useMemo, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { HrpsApiClient } from '../clients/HrpsApiClient';
import {
    BorderedColumn,
    Button,
    GreyBox,
    Heading,
    Input,
    useTranslation,
    TranslatedString,
} from '../components/blocks';
import { CarrierSearchableSelect } from '../components/composites/CarrierSearchableSelect';
import { usePageMessaging } from '../components/composites/PageMessaging';
import { StatusNegative, Add } from '../components/icons';
import { DEFAULT_CARRIERS } from '../constants';
import { useReturnTrackingFeature } from '../hooks/useReturnTrackingFeature';
import { loadCarriers } from '../redux/actions/carriersActionCreators';
import { CreateReturnTrackingInput, ReturnTracking, RootState } from '../redux/types';
import { CodedError } from '../types';
import { Logger } from '../utils/logger';
import {
    selectedOrdersContainerStyle,
    tableHeadersContainerStyle,
    actionButtonStyle,
} from './styles/EnterTrackingInfoPage.styles';

interface EnterTrackingInfoPageProps {
    selectedReturnOrders?: string[];
}

type EnterTrackingInfoPageReduxProps = ConnectedProps<typeof connector>;

interface TrackingRow {
    id: number;
    trackingNumber: string;
    carrierName: string;
    selectedRMA: string;
}

const useTrackingRows = (initialOrders: string[]) => {
    const [rows, setRows] = useState<TrackingRow[]>(() => {
        if (initialOrders.length > 0) {
            return initialOrders.map((orderId, index) => ({
                id: index + 1,
                trackingNumber: '',
                carrierName: '',
                selectedRMA: orderId,
            }));
        }
        return [{ id: 1, trackingNumber: '', carrierName: '', selectedRMA: '' }];
    });

    const addRow = useCallback(() => {
        setRows((prev) => [
            ...prev,
            {
                id: Date.now(),
                trackingNumber: '',
                carrierName: '',
                selectedRMA: '',
            },
        ]);
    }, []);

    const removeRow = useCallback((rowId: number) => {
        setRows((prev) => (prev.length > 1 ? prev.filter((row) => row.id !== rowId) : prev));
    }, []);

    const updateRow = useCallback((rowId: number, field: keyof TrackingRow, value: string) => {
        setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)));
    }, []);

    return { rows, addRow, removeRow, updateRow };
};

const useFormValidation = (rows: TrackingRow[], t: any) => {
    const [validationErrors, setValidationErrors] = useState<Record<number, string[]>>({});

    const validateForm = useCallback((): boolean => {
        const errors: Record<number, string[]> = {};

        rows.forEach((row) => {
            const rowErrors: string[] = [];

            const requiredFields = [
                { field: 'trackingNumber', message: 'Tracking number is required' },
                { field: 'carrierName', message: 'Carrier name is required' },
                { field: 'selectedRMA', message: `${t('rmaId-columnHeader')} is required` },
            ];

            requiredFields.forEach(({ field, message }) => {
                const fieldValue = String(row[field as keyof TrackingRow]);
                if (!fieldValue.trim()) {
                    rowErrors.push(t.cognate(message));
                }
            });

            if (rowErrors.length > 0) {
                errors[row.id] = rowErrors;
            }
        });

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [rows, t]);

    const clearErrorsForRow = useCallback((rowId: number) => {
        setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[rowId];
            return newErrors;
        });
    }, []);

    return { validationErrors, validateForm, clearErrorsForRow };
};

const SelectedOrdersInfo: FC<{ rows: TrackingRow[]; t: any }> = React.memo(({ rows, t }) => {
    const currentRMAs = rows.map((row) => row.selectedRMA).filter((rma) => rma.trim() !== '');
    const orderCount = currentRMAs.length;
    const displayedOrders = currentRMAs.slice(0, 4).join(', ');
    const hasMoreOrders = orderCount > 4;
    const orderText = orderCount > 0 ? `Order ID: ${displayedOrders}${hasMoreOrders ? ', ...' : ''}` : 'Order ID:';

    return (
        <div style={selectedOrdersContainerStyle}>
            <Column spacing={'small'}>
                <Text type={'h200'}>{t('returnOrdersSelected-text', { count: orderCount })}</Text>
                <Text type={'b200'} color={'secondary'}>
                    {t.cognate(orderText)}
                </Text>
            </Column>
        </div>
    );
});

const TableHeaders: FC<{ t: any }> = React.memo(({ t }) => {
    const headerStyle = { width: '33%' };
    const borderStyle = { borderBottom: '1px solid #CCCCCC', marginTop: '4px' };

    const headers = [t('rmaId-columnHeader'), t('carrier-columnHeader'), t('trackingNumber-columnHeader')];

    return (
        <div style={tableHeadersContainerStyle}>
            <div style={{ width: '32px' }} />
            {headers.map((header, index) => (
                <div key={header} style={{ ...headerStyle, marginLeft: index === 0 ? '-16px' : '0' }}>
                    <Text type={'h300'}>{header}</Text>
                    <div style={borderStyle} />
                </div>
            ))}
            <div style={{ width: '32px' }} />
        </div>
    );
});

const ActionButton: FC<{
    icon: React.ReactNode;
    onClick: () => void;
    isHovered: boolean;
    onHover: (hovered: boolean) => void;
    disabled?: boolean;
}> = React.memo(({ icon, onClick, isHovered, onHover, disabled }) => {
    let buttonOpacity = 1;
    if (disabled) {
        buttonOpacity = 0.5;
    } else if (isHovered) {
        buttonOpacity = 0.8;
    }

    const buttonTransform = isHovered && !disabled ? 'scale(1.1)' : 'scale(1)';

    return (
        <div
            role={'button'}
            tabIndex={0}
            style={actionButtonStyle(buttonOpacity, buttonTransform, disabled || false)}
            onClick={disabled ? undefined : onClick}
            onKeyDown={(e) => e.key === 'Enter' && !disabled && onClick()}
            onMouseEnter={() => onHover(true)}
            onMouseLeave={() => onHover(false)}
        >
            {icon}
        </div>
    );
});

const EnterTrackingInfoPage: FC<EnterTrackingInfoPageProps & EnterTrackingInfoPageReduxProps> = ({
    selectedReturnOrders: propSelectedReturnOrders = [],
    reseller,
    carriers,
    loadCarriers,
}) => {
    const { t } = useTranslation('enterTrackingInfoPage');
    const location = useLocation<{ selectedReturnOrders?: string[] }>();
    const history = useHistory();
    const { showError } = usePageMessaging();
    const { isReturnTrackingEnabled } = useReturnTrackingFeature();

    const selectedReturnOrders = location.state?.selectedReturnOrders || propSelectedReturnOrders;

    useEffect(() => {
        if (!isReturnTrackingEnabled) {
            showError(t('error-ReturnTrackingNotAuthorized'));
            history.replace('/returns/list/return-orders');
        }
    }, [isReturnTrackingEnabled, showError, t, history]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [buttonHoverStates, setButtonHoverStates] = useState<Record<string, boolean>>({});

    const { rows, addRow, removeRow, updateRow } = useTrackingRows(selectedReturnOrders);
    const { validationErrors, validateForm, clearErrorsForRow } = useFormValidation(rows, t);

    useEffect(() => {
        if (carriers.status === 'Uninitialized') {
            loadCarriers();
        }
    }, [carriers.status, loadCarriers]);

    const carriersMap = carriers.value || {};
    const isCarriersLoading = carriers.status === 'Loading';
    const carriersOptions = useMemo(() => {
        const entries = Object.entries(carriersMap);
        if (entries.length > 0) {
            return entries.map(([name, _scac]) => ({ value: name, label: name as TranslatedString }));
        }
        return DEFAULT_CARRIERS.map((carrier) => ({ value: carrier.value, label: carrier.label as TranslatedString }));
    }, [carriersMap]);

    const getScacFromCarrierName = useCallback(
        (carrierName: string): string => {
            return carriersMap[carrierName] || carrierName;
        },
        [carriersMap]
    );

    const resellerId = reseller.value?.resellerId;

    const handleHover = useCallback((key: string, hovered: boolean) => {
        setButtonHoverStates((prev) => ({ ...prev, [key]: hovered }));
    }, []);

    const handleRowUpdate = useCallback(
        (rowId: number, field: keyof TrackingRow, value: string) => {
            const limitedValue = value.length > 100 ? value.substring(0, 100) : value;
            updateRow(rowId, field, limitedValue);
            clearErrorsForRow(rowId);
        },
        [updateRow, clearErrorsForRow]
    );

    const transformToApiFormat = useCallback((): CreateReturnTrackingInput => {
        const groupedTracking = rows.reduce((acc, row) => {
            const key = `${row.carrierName}|${row.trackingNumber}`;
            const newAcc = { ...acc };
            newAcc[key] = newAcc[key] || [];
            newAcc[key].push(row.selectedRMA);
            return newAcc;
        }, {} as Record<string, string[]>);

        const returnTrackingList: ReturnTracking[] = Object.entries(groupedTracking).map(([key, rmaIds]) => {
            const [carrierName, carrierTrackingNumber] = key.split('|');
            const scac = getScacFromCarrierName(carrierName);
            return { carrierName, scac, carrierTrackingNumber, rmaIds };
        });

        return {
            resellerId: resellerId || '',
            returnTrackingList,
        };
    }, [rows, resellerId, getScacFromCarrierName]);

    const handleSubmit = useCallback(async () => {
        if (!validateForm() || !resellerId) {
            Logger.debug('Form validation failed or no reseller selected');
            return;
        }

        setIsSubmitting(true);
        try {
            const apiInput = transformToApiFormat();
            const hrpsClient = new HrpsApiClient();
            await hrpsClient.createReturnTracking(apiInput);

            history.push('/returns/list/return-orders', { trackingAddedSuccessfully: true });
        } catch (error) {
            Logger.error('Error creating return tracking:', error);

            const errorCode = (error as CodedError).code || 'Internal';
            showError(t(`error-${errorCode}`));
        } finally {
            setIsSubmitting(false);
        }
    }, [validateForm, resellerId, transformToApiFormat, history, showError, t]);

    return (
        <BorderedColumn alignmentHorizontal={'center'} spacing={'none'}>
            <GreyBox type={'fill'} spacingInset={'small'} width={'100%'}>
                <Heading level={2} alignment={'left'}>
                    {t('pageHeading')}
                </Heading>
            </GreyBox>

            <Column spacing={'medium'} width={'900px'}>
                <div style={{ padding: '20px' }}>
                    <SelectedOrdersInfo rows={rows} t={t} />
                    <TableHeaders t={t} />

                    {rows.map((row) => (
                        <div key={row.id} style={{ marginBottom: '16px' }}>
                            <Row spacing={'large'} alignmentVertical={'top'}>
                                <div style={{ width: '20px' }} />

                                <Column spacing={'small'} width={'33%'}>
                                    <Input
                                        value={row.selectedRMA}
                                        onChange={(value) => handleRowUpdate(row.id, 'selectedRMA', value)}
                                        placeholder={t('enterRmaId-placeholder')}
                                        size={'medium'}
                                        width={'100%'}
                                    />
                                </Column>

                                <Column spacing={'small'} width={'33%'}>
                                    <CarrierSearchableSelect
                                        value={row.carrierName}
                                        onChange={(value) => handleRowUpdate(row.id, 'carrierName', value)}
                                        selectOptions={carriersOptions}
                                        placeholder={t('selectCarrier-placeholder')}
                                        isDisabled={isCarriersLoading}
                                        limit={10}
                                        minSearchChars={1}
                                    />
                                </Column>

                                <Column spacing={'small'} width={'33%'}>
                                    <Input
                                        value={row.trackingNumber}
                                        onChange={(value) => handleRowUpdate(row.id, 'trackingNumber', value)}
                                        placeholder={t('enterTrackingNumber-placeholder')}
                                        size={'medium'}
                                        width={'100%'}
                                    />
                                </Column>

                                {rows.length > 1 ? (
                                    <ActionButton
                                        icon={<StatusNegative />}
                                        onClick={() => removeRow(row.id)}
                                        isHovered={buttonHoverStates[`remove-${row.id}`] || false}
                                        onHover={(hovered) => handleHover(`remove-${row.id}`, hovered)}
                                    />
                                ) : (
                                    <div style={{ width: '32px' }} />
                                )}
                            </Row>

                            {validationErrors[row.id] && (
                                <div style={{ marginTop: '8px', marginLeft: '52px' }}>
                                    {validationErrors[row.id].map((error) => (
                                        <div key={`error-${row.id}-${error}`} style={{ marginBottom: '4px' }}>
                                            <Text type={'b100'} color={'error'}>
                                                {error}
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}

                    <div style={{ marginTop: '32px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Button type={'primary'} onClick={addRow} disabled={isSubmitting}>
                                <Add />
                            </Button>
                            <Row spacing={'medium'} alignmentHorizontal={'right'}>
                                <Button type={'secondary'} onClick={() => history.goBack()} disabled={isSubmitting}>
                                    <p>{t('back-buttonLabel')}</p>
                                </Button>
                                <Button
                                    type={'primary'}
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    isBusy={isSubmitting}
                                >
                                    <p>{isSubmitting ? t('submitting-buttonLabel') : t('submit-buttonLabel')}</p>
                                </Button>
                            </Row>
                        </div>
                    </div>
                </div>
            </Column>
        </BorderedColumn>
    );
};

const mapStateToProps = (state: RootState) => ({
    reseller: state.resellerReducer.selectedReseller,
    carriers: state.carriersReducer.carriers,
});

const mapDispatchToProps = {
    loadCarriers,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(EnterTrackingInfoPage);
